import React from 'react';
import PropTypes from 'prop-types';
import { compose, withState, withHandlers } from 'recompose';
import { FormattedMessage } from 'react-intl';

import connect from 'app/libs/connect';
import EmailField from 'app/features/authentication/components/EmailField';
import PasswordField from 'app/features/authentication/components/PasswordField';
import S from 'app/features/authentication/components/AuthForm.styled';
import noop from 'app/libs/noop';
import Button from 'app/components/Button/Button';
import Heading from 'app/components/layout/Heading.styled';
import RecaptchaContainer from 'app/features/authentication/components/Recaptcha/RecaptchaContainer';
import { isAuthenticationLoading } from 'app/features/authentication/selectors';
import Checkbox from 'app/components/Checkbox/Checkbox';
import Text from 'app/components/layout/Text.styled';
import withDispatchOnMount from 'app/enhancers/withDispatchOnMount';
import { selectSkuFromGetParams } from 'app/features/products/actions';
import { setFlashMessage } from 'app/features/flashMessage/actions';

const onSubmit = (event, handleSubmit) => {
  event.preventDefault();
  handleSubmit();
};

const AuthForm = ({
  shouldShowCaptcha,
  setCaptcha,
  setCaptchaField,
  headingTitleKey,
  buttonTextKey,
  emailErrorKey,
  emailFloatingTextKey,
  validateEmail,
  passwordErrorKey,
  passwordFloatingTextKey,
  passwordWithSpaceTextKey,
  handleSubmit,
  setUserName,
  username,
  setPassword,
  password,
  isCreateAccount,
  isOptInChecked,
  onOptInCheckboxChange,
  isLoading,
  formType,
}) => (
  <S.AuthForm
    data-t="auth-form"
    onSubmit={event => onSubmit(event, handleSubmit)}
  >
    <Heading size="l" as={S.AuthForm.Title}>
      <FormattedMessage id={headingTitleKey} />
    </Heading>
    <S.AuthForm.InputsContainer isCreateAccount={isCreateAccount}>
      <EmailField
        errorTextKey={emailErrorKey}
        floatingTextKey={emailFloatingTextKey}
        onEmailChange={setUserName}
        validateEmail={validateEmail}
        testId="auth-form-user-name-field"
      />
      <PasswordField
        errorTextKey={passwordErrorKey}
        floatingTextKey={passwordFloatingTextKey}
        passwordWithSpaceTextKey={passwordWithSpaceTextKey}
        onPasswordChange={setPassword}
        testId="auth-form-password-field"
      />
      {shouldShowCaptcha && (
        <RecaptchaContainer
          onCaptchaChange={setCaptcha}
          onCaptchaFieldChange={setCaptchaField}
          handleSubmit={handleSubmit}
          username={username}
          password={password}
          formType={formType}
        />
      )}
    </S.AuthForm.InputsContainer>
    {isCreateAccount && (
      <S.AuthForm.OptInCheckboxContainer>
        <Checkbox
          isChecked={isOptInChecked}
          onChange={onOptInCheckboxChange}
          dataT="opt-in-checkbox"
        >
          <Text size="m">
            <FormattedMessage id="registration_email_opn_in" />
          </Text>
        </Checkbox>
      </S.AuthForm.OptInCheckboxContainer>
    )}
    <Button
      onClick={handleSubmit}
      type="one"
      isWide
      testId="auth-form-submit-button"
      isLoading={isLoading}
    >
      <FormattedMessage id={buttonTextKey} />
    </Button>
    <S.AuthForm.SubmitInput formNoValidate type="submit" />
  </S.AuthForm>
);

AuthForm.propTypes = {
  shouldShowCaptcha: PropTypes.bool.isRequired,
  headingTitleKey: PropTypes.string.isRequired,
  buttonTextKey: PropTypes.string.isRequired,
  emailErrorKey: PropTypes.string.isRequired,
  emailFloatingTextKey: PropTypes.string.isRequired,
  passwordErrorKey: PropTypes.string.isRequired,
  passwordFloatingTextKey: PropTypes.string.isRequired,
  passwordWithSpaceTextKey: PropTypes.string.isRequired,
  validateEmail: PropTypes.func.isRequired,
  setUserName: PropTypes.func,
  username: PropTypes.string,
  setPassword: PropTypes.func,
  password: PropTypes.string,
  setCaptcha: PropTypes.func.isRequired,
  setCaptchaField: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  isCreateAccount: PropTypes.bool,
  isOptInChecked: PropTypes.bool,
  onOptInCheckboxChange: PropTypes.func,
  formType: PropTypes.string.isRequired,
};

AuthForm.defaultProps = {
  handleSubmit: noop,
  setUserName: noop,
  username: '',
  setPassword: noop,
  password: '',
  isLoading: false,
  isCreateAccount: false,
  isOptInChecked: false,
  onOptInCheckboxChange: noop,
};

const handleSubmit =
  ({
    username,
    password,
    isOptInChecked,
    formHandler,
    captcha,
    setShouldShowCaptcha,
    isCreateAccount,
    setCaptcha,
    captchaField,
    dispatch,
  }) =>
  () => {
    if (!captcha) {
      setShouldShowCaptcha(true);
      dispatch(
        setFlashMessage({
          messageId: 'flashmessage_error_registration_bad_request',
          statusType: 'error',
        }),
      );
    } else {
      formHandler({
        username,
        password,
        ...(isCreateAccount && {
          opt_in_to_marketing: Number(isOptInChecked),
        }),
        ...(captchaField ? { [captchaField]: captcha } : {}),
      });
      setShouldShowCaptcha(false);
      setCaptcha('');
    }
  };

export { AuthForm, onSubmit, handleSubmit };

export default compose(
  connect({ isLoading: isAuthenticationLoading }),
  withDispatchOnMount(selectSkuFromGetParams),
  withState('username', 'setUserName', ''),
  withState('password', 'setPassword', ''),
  withState('shouldShowCaptcha', 'setShouldShowCaptcha', true),
  withState('captcha', 'setCaptcha', ''),
  withState('captchaField', 'setCaptchaField', ''),
  withHandlers({ handleSubmit }),
)(AuthForm);
